import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { WHATSAPP_URL, trackWhatsAppClick, PACKAGES } from "@/lib/site";
import { trackEvent, trackContact } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","District of Columbia",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland",
  "Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington",
  "West Virginia","Wisconsin","Wyoming","Other / Outside USA",
];

const CONCERNS = [
  "Autism", "ADHD", "Speech Delay", "Developmental Concerns",
  "Sensory Concerns", "Attention / Behavior", "Sleep / Digestion", "Other",
];

const CHILD_AGES = ["Under 3", "3–5", "6–9", "10–13", "14–17"];

const PACKAGE_OPTIONS = [PACKAGES.p350, PACKAGES.p600, PACKAGES.p1000, PACKAGES.unsure];

const inputCls =
  "w-full bg-white border border-[#E2DACD] rounded-xl px-4 py-3 text-sm text-[#2D3A32] placeholder:text-[#9aa79e] focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059] transition";

const Label = ({ children, required }) => (
  <label className="block text-xs font-semibold tracking-wide text-[#1B3B2B] mb-1.5">
    {children} {required && <span className="text-[#B85D43]">*</span>}
  </label>
);

export default function LeadForm({ selectedPackage }) {
  const [form, setForm] = useState({
    parent_name: "", email: "", phone: "", country: "United States",
    state: "", child_age: "", package: "", city: "", message: "",
  });
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (selectedPackage) setForm((f) => ({ ...f, package: selectedPackage }));
  }, [selectedPackage]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleConcern = (c) =>
    setConcerns((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (concerns.length === 0) {
      toast.error("Please select at least one area of support.");
      return;
    }
    if (!form.package) {
      toast.error("Please select a consultation pathway.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, concerns });
      trackContact({ content_name: "USA Consultation Request", package: form.package, concerns: concerns.join(",") });
      trackEvent("ConsultationFormSubmit", { package: form.package });
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again or message us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lead-form" data-testid="lead-form-section" className="bg-[#EAF0EC] pt-16 pb-28 lg:py-28">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <Eyebrow>
            <span className="mx-auto">Begin · Request Your Consultation</span>
          </Eyebrow>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1B3B2B] tracking-tight leading-tight">
            Request Your USA Consultation
          </h2>
        </Reveal>

        <Reveal delay={0.12} className="mt-10">
          <div className="bg-[#FAF7F2] rounded-3xl border border-[#E5D3AD]/60 shadow-[0_24px_60px_rgb(27,59,43,0.1)] p-6 sm:p-10">
            {submitted ? (
              <div data-testid="lead-form-success" className="text-center py-10">
                <span className="mx-auto w-16 h-16 rounded-full bg-[#EAF0EC] flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-[#1B3B2B]" />
                </span>
                <h3 className="mt-6 font-serif text-2xl sm:text-3xl text-[#1B3B2B]">
                  Thank you. Your consultation request has been received.
                </h3>
                <p className="mt-4 text-sm sm:text-base text-[#5C6E62] max-w-md mx-auto leading-relaxed">
                  Our team will contact you shortly.
                </p>
                <a
                  data-testid="success-whatsapp-btn"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick("success_panel")}
                  className="mt-8 inline-flex items-center gap-2 bg-[#1B3B2B] text-[#FAF7F2] text-sm font-semibold px-7 py-3.5 rounded-full hover:bg-[#132C20] transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> Message Us on WhatsApp
                </a>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="lead-form" className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label required>Parent Name</Label>
                    <input data-testid="lead-parent-name-input" required className={inputCls} placeholder="First & Last Name" value={form.parent_name} onChange={set("parent_name")} />
                  </div>
                  <div>
                    <Label required>Email Address</Label>
                    <input data-testid="lead-email-input" required type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={set("email")} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label required>Phone / WhatsApp Number</Label>
                    <input data-testid="lead-phone-input" required className={inputCls} placeholder="+1 ..." value={form.phone} onChange={set("phone")} />
                  </div>
                  <div>
                    <Label required>Country</Label>
                    <input data-testid="lead-country-input" required className={inputCls} value={form.country} onChange={set("country")} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label required>State</Label>
                    <select data-testid="lead-state-select" required className={inputCls} value={form.state} onChange={set("state")}>
                      <option value="">Select your state</option>
                      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label required>Child's Age</Label>
                    <select data-testid="lead-child-age-select" required className={inputCls} value={form.child_age} onChange={set("child_age")}>
                      <option value="">Select age range</option>
                      {CHILD_AGES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <Label required>What would you like support with?</Label>
                  <div data-testid="lead-concerns-group" className="flex flex-wrap gap-2">
                    {CONCERNS.map((c) => (
                      <button
                        type="button"
                        key={c}
                        data-testid={`concern-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        onClick={() => toggleConcern(c)}
                        className={`text-xs font-medium px-4 py-2.5 rounded-full border transition-all duration-300 ${
                          concerns.includes(c)
                            ? "bg-[#1B3B2B] text-[#FAF7F2] border-[#1B3B2B]"
                            : "bg-white text-[#2D3A32] border-[#E2DACD] hover:border-[#C5A059]"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Which consultation pathway are you interested in?</Label>
                  <div data-testid="lead-package-group" className="space-y-2.5">
                    {PACKAGE_OPTIONS.map((p) => (
                      <label
                        key={p}
                        data-testid={`package-option-${p.slice(0, 6).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 cursor-pointer transition-all duration-300 ${
                          form.package === p ? "border-[#C5A059] bg-[#F4EFE6]" : "border-[#E2DACD] bg-white hover:border-[#C5A059]/60"
                        }`}
                      >
                        <input
                          type="radio"
                          name="package"
                          className="accent-[#1B3B2B] w-4 h-4"
                          checked={form.package === p}
                          onChange={() => setForm((f) => ({ ...f, package: p }))}
                        />
                        <span className="text-sm text-[#2D3A32]">{p}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Preferred Consultation City</Label>
                  <input data-testid="lead-city-input" className={inputCls} placeholder="e.g., Dallas, TX (cities to be announced)" value={form.city} onChange={set("city")} />
                </div>
                <div>
                  <Label>Message / Additional Information</Label>
                  <textarea data-testid="lead-message-input" rows={3} className={inputCls} placeholder="Anything you'd like us to know (optional)" value={form.message} onChange={set("message")} />
                </div>

                <button
                  data-testid="lead-form-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1B3B2B] text-[#FAF7F2] font-semibold tracking-wide text-sm px-8 py-4.5 py-4 rounded-full hover:bg-[#132C20] transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  REQUEST MY CONSULTATION
                </button>
                <p className="text-[11px] text-[#8a968e] text-center leading-relaxed">
                  Your information is used only to contact you regarding your consultation request.{" "}
                  <button
                    type="button"
                    data-testid="lead-privacy-link"
                    onClick={() => document.getElementById("footer-privacy-btn")?.click()}
                    className="underline underline-offset-2 hover:text-[#1B3B2B]"
                  >
                    Privacy Policy
                  </button>
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
